import { useEffect, useState } from "react";
import { TableData } from "../../../components/Table/DataTable";
import { classificationMethodMapping, regressionMethodMapping } from "../../../utils/supplementalData";
import { ReactComponent as ChartIcon } from '../../../assets/Icons/adornments/BarChart.svg';
import Plot from "react-plotly.js";
import { config } from "../../../utils/plot";
import { Skeleton } from "@mui/material";

const rgbColors = {
    BernoulliNB: 'rgba(44, 114, 142, 0.1)',
    MLPClassifier: 'rgba(33, 144, 140, 0.1)',
    AdaBoostClassifier: '#rgba(59, 82, 139, 0.1)',
    KNeighborsClassifier: 'rgba(39, 173, 129, 0.1)',
    LogisticRegression: 'rgba(93, 200, 99, 0.1)',
    RandomForestClassifier: 'rgba(170, 220, 50, 0.1)',
    SVC: 'rgba(253, 231, 37, 0.1)',
    XGBClassifier: 'rgba(255, 197, 111, 0.1)',
}

const trColors = {
    BernoulliNB: '#2C728E',
    MLPClassifier: '#21908C',
    AdaBoostClassifier: '#3B528B',
    KNeighborsClassifier: '#27AD81',
    LogisticRegression: '#5DC863',
    RandomForestClassifier: '#AADC32',
    SVC: '#FDE725',
    XGBClassifier: '#FFC56F'
}

const regTrColors = {
    r2: '#DB5C68',
    mae: '#B8328A',
    rmse: '#5802A3',
}

const layout_radar: () => Partial<Plotly.Layout> = function() {
    return {
        autosize: true,
        polar: {
            radialaxis: {
                visible: true,
                range: [-1,1],
            }
        },
        legend: {
            font: {
                size: 12,
                family: 'Montserrat',
            },
        },
        margin: {
            t: 40,
            b: 0,
            l: 40
        }
    }
}

const layout_bar_sublot: () => Partial<Plotly.Layout> = function() {
    return {
        barmode: 'group',
        autosize: true,
        yaxis: {range: [0, 1], autorange: false, dtick: 0.2},
        yaxis2: {range: [0, 1], autorange: false, dtick: 0.2},
        xaxis2: {anchor: 'y2'},
        showlegend: true,
        legend: {
            font: {
                size: 12,
                family: 'Montserrat',
            },
            tracegroupgap: 1,
        },
        shapes: [
            {
                type: 'line',
                x0: 0,
                y0: 1,
                x1: 1,
                y1: 1,
                xref: 'paper',
                yref: 'y',
                line: {
                    color: 'blue',
                    width: 3,
                    dash: 'dot',
                }
            },
            {
                type: 'line',
                x0: 0,
                y0: 1,
                x1: 1,
                y1: 1,
                xref: 'paper',
                yref: 'y2',
                line: {
                    color: 'blue',
                    width: 3,
                    dash: 'dot',
                }
            },
        ],
        margin: {
            t: 40,
            b: 64,
            l: 40,
            r: 0,
        },
        grid: {
            rows: 2,
            columns: 1,
            ygap: 0.2,
        }
    }
}

const layout_bar: (maxValue: number) => Partial<Plotly.Layout> = function(maxValue) {
    return {
        barmode: 'group',
        yaxis: {
            range: [-1,maxValue ? maxValue : 1], 
            autorange: false,
            dtick: maxValue > 10 ? 1 : 0.2,
        },
        showlegend: true,
        legend: {
            font: {
                size: 12,
                family: 'Montserrat',
            }
        },
        margin: {
            t: 40,
            b: 64,
            l: 40,
            r: 0,
        }
    }
};

const getMinMax = (data: TableData[]) => {
    const values: number[] = [];

    let minValue = 0;
    let maxValue = 1;

    data.forEach(model => {
        const r2Field = model.fields.find(field => field.associatedColumn === 'r2');
        const maeField = model.fields.find(field => field.associatedColumn === 'mae');
        const rmseField = model.fields.find(field => field.associatedColumn === 'rmse');

        [r2Field, maeField, rmseField].forEach(field => {
            if (field) {
                const fieldValue = Number(field.value);
                if (!isNaN(fieldValue)) {
                    values.push(fieldValue);
                }
            }
        });
    });

    minValue = Math.min(0, ...values);
    maxValue = Math.max(1,...values);

    return {
        minValue,
        maxValue,
    }
}


export default function ModelComparisonV2({
    data,
    isLoading,
}: {
    data: TableData[],
    isLoading: boolean,
}) {
    const [ barSubplots, setBarSubplots ] = useState<any[]>([]);
    const [ barLayout, setBarLayout ] = useState<Partial<Plotly.Layout>>({});
    const [ radarData, setRadarData ] = useState<any[] | null>(null);

    useEffect(() => {
        if (data.length > 0) {

            let methods: {
                name: string,
                stats: {
                    [key: string]: number,
                }
            }[] = [];
            let sorted: {
                name: string,
                stats: {
                    [key: string]: number,
                }
            }[] = [];

            data.forEach(model => {
                const methodNameField = model.fields.find(field => field.associatedColumn === 'method');
                const statFields = model.fields.filter(field => field.associatedColumn !== 'method');
                if (methodNameField) {
                    let statsObj = {} as any;
                    statFields.forEach(field => {
                        statsObj[field.associatedColumn] = field.value;
                    })
                    methods.push({
                        name: methodNameField.value as string,
                        stats: statsObj,
                    })
                }
            })

            if (methods.length > 0) {
                sorted = methods.sort((a,b) => {
                    if (a.name.toLowerCase() < b.name.toLowerCase()) {
                        return -1
                    } else if (a.name.toLowerCase() > b.name.toLowerCase()) {
                        return 1
                    } else {
                        return 0
                    }
                })
            }


            let methodArr: string[] = [];
            const referenceStats = methods.length > 0 ? methods[0].stats : {};
            const hasClassificationStats = ['auc', 'acc', 'precision', 'recall', 'f1score', 'mcc', 'specificity'].some((key) => key in referenceStats);


            if (hasClassificationStats) {

                methodArr = ['auc', 'acc', 'cohens_kappa', 'f1score', 'mcc', 'precision', 'recall', 'specificity'];

                const firstMethodArr = methodArr.slice(0, 4);
                const secondMethodArr = methodArr.slice(4);

                const subplots: any[] = [];

                sorted.forEach(m => {
                    subplots.push({
                        x: firstMethodArr,
                        y: firstMethodArr.map(method => m.stats[method as keyof object]),
                        name: classificationMethodMapping[m.name as keyof object] ?? m.name, 
                        type: 'bar',
                        marker: {
                            color: trColors[m.name as keyof object],
                        },
                        legendgroup: m.name,
                        showlegend: true,
                    })
                });
                 
                sorted.forEach(m => {
                    subplots.push({
                        x: secondMethodArr,
                        y: secondMethodArr.map(method => m.stats[method as keyof object]),
                        name: classificationMethodMapping[m.name as keyof object] ?? m.name,
                        type: 'bar',
                        marker: {
                            color: trColors[m.name as keyof object],
                        },
                        xaxis: 'x2',
                        yaxis: 'y2',
                        legendgroup: m.name,
                        showlegend: false,
                    })
                });

    
                const radarArr = sorted.map(m => ({
                    type: 'scatterpolar',
                    r: methodArr.map(method => m.stats[method as keyof object]),
                    theta: methodArr,
                    fill: 'toself',
                    name: classificationMethodMapping[m.name as keyof object] ?? m.name,
                    marker: {
                        color: trColors[m.name as keyof object],
                    },
                    fillcolor: rgbColors[m.name as keyof object],
                    showlegend: true,
                }));

                setRadarData(radarArr);
                setBarSubplots(subplots);
                setBarLayout(layout_bar_sublot());
                
            } else {
                methodArr = ['r2', 'mae', 'rmse'];

                const traceArr = methodArr.map(method => ({
                    x: sorted.map(m => regressionMethodMapping[m.name as keyof object] ?? m.name),
                    y: sorted.map(m => m.stats[method as keyof object]),
                    name: method,
                    type: 'bar',
                    marker: {
                        color: regTrColors[method as keyof object],
                    }
                }));

                const { maxValue } = getMinMax(data);
    
                setBarSubplots(traceArr);
                setBarLayout(layout_bar(maxValue));
                setRadarData(null);

            }

    
        }

    }, [data]);

    
    return (
        <div className="w-full flex flex-col justify-start items-stretch bg-white rounded-2xl border border-outline">
            <div className="flex gap-2 p-6">
                {isLoading ? (
                    <Skeleton variant='text' sx={{fontSize: '20px'}} width={200}/>
                ) : (
                <>
                    <div className="w-[20px] text-disable"><ChartIcon /></div>
                    <p className="text-default font-semibold text-[20px]">Model Comparison</p>
                </>
                )}
            </div>
            <div className="w-full flex justify-between items-center gap-6 h-[650px] p-6">
                {isLoading ? (
                    <Skeleton variant='rounded' width='100%' height='100%' />
                ) : (
                <>
                    <Plot 
                        data={barSubplots}
                        layout={barLayout}
                        config={config}
                        className={`w-full h-full grow`}
                    />
                    {radarData && (
                        <Plot 
                            data={radarData} 
                            layout={layout_radar()} 
                            config={config} 
                            className={`w-auto aspect-square h-full`}
                        />
                    )}
                </>
                )}
            </div>
        </div>
    )
}